import * as React from 'react'
import * as SliderPrimitive from '@radix-ui/react-slider'

import { cn } from '@/helpers/utils'
import { useEffect, useRef, useState } from 'react'

const SliderWithTicks = React.forwardRef<
  React.ElementRef<typeof SliderPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root> & {
    stops: number
  }
>(({ className, stops, value, onValueChange, ...props }, ref) => {
  const initialValue = value ?? [0]
  const [sliderValue, setSliderValue] = useState<number[]>(initialValue)
  const rangeRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // sync slider if the value prop changes
    if (value && value.length > 0) {
      setSliderValue(value)
    }
  }, [value])

  const tickMarks = Array.from({ length: stops }, (_, i) => `tick-mark-${i}`)

  const calculateTickLeftPercent = (index: number) => {
    if (stops === 0) {
      return 0
    }
    if (stops === 1) {
      return 100
    }

    // Calculate the percentage from left of the slider's width
    // example: 0, 20, 40, 60, 80, 100 if numberTickMarks is 5
    return (100 * index) / (stops - 1)
  }

  const isSliderPastTick = (markPosition: number) => {
    const rightPercentFromSlider = rangeRef.current?.style.right
    // FIXME: this means the slider is not mounted yet
    if (!rightPercentFromSlider) {
      return false
    }
    // always true since the slider starting position is 0
    if (markPosition === 0) {
      return true
    }
    // Note: assumes the slider doesn't exceed 100%
    const sliderOffsetFromLeft = 100 - parseFloat(rightPercentFromSlider)

    return sliderOffsetFromLeft > markPosition
  }

  const handleSliderValueChange = (value: number[]) => {
    setSliderValue(value)

    // update parent component
    onValueChange?.(value)
  }

  return (
    <SliderPrimitive.Root
      ref={ref}
      className={cn(
        'relative flex w-full touch-none select-none items-center data-[disabled]:opacity-50',
        className,
      )}
      {...props}
      value={sliderValue}
      onValueChange={handleSliderValueChange}
    >
      {/* NOTE: h-4 to increase the hit area of the slider */}
      <SliderPrimitive.Track className="relative flex h-4 w-full grow items-center justify-center overflow-hidden">
        <div className="absolute h-1 w-full rounded-full bg-neutral-light"></div>
        <SliderPrimitive.Range
          className="absolute h-1 bg-primary-light"
          ref={rangeRef}
        />
        {/* add tick marks to range */}
        {tickMarks.map((mark, markIndex) => {
          const positionLeft = calculateTickLeftPercent(markIndex)
          const isCovered = isSliderPastTick(positionLeft)

          return (
            <div
              key={mark}
              className={cn(
                'absolute h-0.5 w-0.5 rounded-full bg-primary-light transition-colors',
                // if the tick mark is covered by the range, add a lighter color
                isCovered && 'bg-primary-lighter/40',
                // NOTE: the first and last ticks are offset by 0.5px so they appear on the range
                markIndex === 0 && 'translate-x-0.5',
                markIndex === tickMarks.length - 1 && '-translate-x-0.5',
              )}
              style={{
                left: `${positionLeft}%`,
              }}
            />
          )
        })}
      </SliderPrimitive.Track>

      {/* TODO: should have hover effect? */}
      <SliderPrimitive.Thumb className="block h-5 w-5 cursor-pointer rounded-full border border-primary-light bg-background ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 data-[disabled]:pointer-events-none data-[disabled]:bg-background" />
    </SliderPrimitive.Root>
  )
})
SliderWithTicks.displayName = SliderPrimitive.Root.displayName

export { SliderWithTicks }
